import {
  enableProdMode,
  provideAppInitializer,
  inject,
  importProvidersFrom,
  provideZoneChangeDetection,
} from '@angular/core';
import { environment } from './environments/environment';
import { MessageService, ConfirmationService } from '@openng/optimus-ui/api';
import { CanDeactivateGuard } from './app/guards/deactivate.guard';
import { DialogService } from '@openng/optimus-ui/dynamicdialog';
import { CalendarService } from './app/core/services/calendar.service';
import { TabService } from './app/core/services/tab.service';
import { provideOptimus } from '@openng/optimus-ui/config';
import { CrlincTheme } from './app/crlinc-theme';
import { provideOktaAuth, withOktaConfig } from '@okta/okta-angular';
import {
  provideHttpClient,
  withInterceptorsFromDi,
  withXsrfConfiguration,
  withFetch,
} from '@angular/common/http';
import { EnvironmentService } from './app/services/environment.service';
import {
  provideRouter,
  withComponentInputBinding,
  RouterOutlet,
} from '@angular/router';
import { routes } from './app/app.routes';
import { BrowserModule, bootstrapApplication } from '@angular/platform-browser';
import { MessageModule } from '@openng/optimus-ui/message';
import { ToastModule } from '@openng/optimus-ui/toast';
import { DialogModule } from '@openng/optimus-ui/dialog';
import { BreadcrumbModule } from '@openng/optimus-ui/breadcrumb';
import { DrawerModule } from '@openng/optimus-ui/drawer';
import { AutoCompleteModule } from '@openng/optimus-ui/autocomplete';
import { CardModule } from '@openng/optimus-ui/card';
import { FormsModule } from '@angular/forms';
import { ConfirmDialogModule } from '@openng/optimus-ui/confirmdialog';
import { BlockUIModule } from '@openng/optimus-ui/blockui';
import { FullCalendarModule } from '@fullcalendar/angular';
import { PaginatorModule } from '@openng/optimus-ui/paginator';
import { TabsModule } from '@openng/optimus-ui/tabs';
import { ButtonModule } from '@openng/optimus-ui/button';
import { LoggerModule, NgxLoggerLevel } from 'ngx-logger';
import { provideTranslateService } from '@ngx-translate/core';
import { provideTranslateHttpLoader } from '@ngx-translate/http-loader';
import { ServiceWorkerModule } from '@angular/service-worker';
import { AppComponent } from './app/app.component';
import OktaAuth from '@okta/okta-auth-js';

const oktaAuth = new OktaAuth({
  issuer: environment.oktaAuthIssuerURL,
  clientId: environment.oktaClientId,
  redirectUri: window.location.origin + '/authorization-code/callback',
  responseType: ['id_token'],
  pkce: true,
  scopes: ['email', 'profile', 'openid', 'crlinc'],
  tokenManager: {
    autoRenew: true,
    expireEarlySeconds: 300, // Refreshes auth token 300 seconds (5 minutes) before it expires
  },
});

if (environment.production) {
  enableProdMode();
}

bootstrapApplication(AppComponent, {
  providers: [
    provideZoneChangeDetection(),
    importProvidersFrom(
      BrowserModule,
      MessageModule,
      ToastModule,
      DialogModule,
      BreadcrumbModule,
      DrawerModule,
      AutoCompleteModule,
      CardModule,
      FormsModule,
      ToastModule,
      ConfirmDialogModule,
      BlockUIModule,
      FullCalendarModule,
      PaginatorModule,
      TabsModule,
      ButtonModule,
      LoggerModule.forRoot({ level: NgxLoggerLevel.DEBUG }),

      ServiceWorkerModule.register('ngsw-worker.js', {
        enabled: environment.production,
        // Register the ServiceWorker as soon as the app is stable
        // or after 30 seconds (whichever comes first).
        registrationStrategy: 'registerWhenStable:30000',
      }),
      RouterOutlet
    ),
    MessageService,
    ConfirmationService,
    CanDeactivateGuard,
    DialogService,
    CalendarService,
    TabService,
    provideOptimus({
      theme: {
        preset: CrlincTheme,
        options: {
          darkModeSelector: '.crlinc-dark',
        },
      },
    }),
    provideOktaAuth(withOktaConfig({ oktaAuth })),
    provideHttpClient(
      withInterceptorsFromDi(),
      withXsrfConfiguration({
        cookieName: 'XSRF-TOKEN',
        headerName: 'X-XSRF-TOKEN',
      }),
      withFetch()
    ),
    // need to load the environment service first so we can get at the API URL.
    provideAppInitializer(() => {
      return ((envSettings: EnvironmentService) => {
        envSettings.getSettings();
      })(inject(EnvironmentService));
    }),
    provideRouter(routes, withComponentInputBinding()),
    provideTranslateService({
      fallbackLang: 'en',
      loader: provideTranslateHttpLoader({
        prefix: '/assets/i18n/',
        suffix: '.json',
      }),
    }),
  ],
})
  .then(() => {
    if ('serviceWorker' in navigator && environment.production) {
      navigator.serviceWorker.register('ngsw-worker.js');
    } else {
    }
  })
  .catch((err) => console.error(err));
